import { FC, useEffect, useRef, useState } from 'react'
import { Dialog } from "@mui/material";
import CodeUploader from './uploadCode/codeUploader';
import BugsAndErrorReport from './uploadCode/bugsAndErrorReport';
import DocInTinyMce from './uploadCode/docInTinyMce';
import DocumentationModeSelection from './documentationModeSelection';
import CloseIcon from '@mui/icons-material/Close';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { AMDTELECOM_ATLASSIAN_URL_WITH_BROWSE } from '../../services/const';
import { generateOpenProjectTaskUrl } from '../../utils/issues';
import ShowFunctionalities from './uploadCode/showFunctionalities';
import ShowEstimation from './uploadCode/showEstimation';
import { setCurrentDocumentationSteps, setDocumentationContent, setOpenCreateDocumentation, setSelectedDocumentSteps } from '../../store/documentSlice';
import EditDocument from './uploadCode/editDocument';
import ArticleLink from './uploadCode/ArticleLink';
import './style.scss'
import { addJiraCommentForTask, updateConfluenceLinkField } from '../../services/jira.service';
import { toast } from 'react-toastify';

const cleanupDocsChannel = new BroadcastChannel('cleanup_docs_channel');

interface ConvertedContent {
  [key: string]: string;
}

const CreateDocumentation: FC = () => {
  const issueDetails = useSelector(
    (state: RootState) => state.issue,
    shallowEqual
  );
  const userState = useSelector((state: RootState) => state.user);
  const userDetails = userState?.user;
  const projectState = useSelector((state: RootState) => state.project);
  const projectTypeMenu = projectState.projectTypeMenu;
  const projectType = projectState.projectType;
  const documentState = useSelector((state: RootState) => state.document);
  const dispatch = useDispatch();
  const { selectedDocumentSteps, currentDocumentationSteps } = documentState

  const [bugsAndErrorHandlingReport, setBugsAndErrorHandlingReport] = useState<any>({})
  const [jsonConvertedContent, setJsonConvertedContent] = useState<ConvertedContent>({});
  const [reportFeedback, setReportFeedback] = useState<any>({});
  const [requiredIssueDetails, setRequiredIssueDetails] = useState<any>({})
  const [savedFile, setSavedFile] = useState<any>(null);
  const [coreFunctionalities, setCoreFunctionalities] = useState<any>(null);
  const [estimationTimer, setEstimationTimer] = useState<number>(0)
  const [triggerTimer, setTriggerTimer] = useState<boolean>(false)
  const [articleLink, setArticleLink] = useState<string>("")
  const timerRef = useRef<number | null>(null)

  const resetUpload = () => {
    setJsonConvertedContent({})
    setReportFeedback({})
    if (["editDoc", "articlePage"].includes(selectedDocumentSteps[currentDocumentationSteps]))
      dispatch(setDocumentationContent({
        name: "",
        description: "",
        additional_notes: "",
        text: "",
        article_id: "",
        core_functionalities: [],
        userId: ""
      }))
  }

  useEffect(() => {
    if (issueDetails) {
      setRequiredIssueDetails(
        {
          "id": issueDetails.id,
          "taskLink": projectType === projectTypeMenu[0] ? generateOpenProjectTaskUrl(issueDetails.issueData?.id) : projectType === projectTypeMenu[2] ? AMDTELECOM_ATLASSIAN_URL_WITH_BROWSE + issueDetails.key : issueDetails.key,
          "issueType": issueDetails.issueType,
          "project": issueDetails.projectLink,
          "key": issueDetails.key,
          "emailAddress": userDetails?.userDetails?.email_address,
          "organizationalChartId": sessionStorage.getItem("chartId") || "",
          "userId": userDetails?.userDetails?.id
        }
      )
    }
  }, [issueDetails])

  const handleAddArticleLinkToJira = async (articleLink: any) => {
    try {
      const commentPayload = {
        issueKey: requiredIssueDetails?.key,
        body: `Documentation: ${articleLink}`
      }
      const customFieldPayload = {
        issueKey: requiredIssueDetails?.key,
        articleLink: articleLink
      }
      await addJiraCommentForTask(commentPayload);
      await updateConfluenceLinkField(customFieldPayload);
      toast.success('Comment and confluence link Added successfully');
    } catch (error) {
      toast.error('Error adding article link to Jira issue');
    }
  }

  const handleClosePopup = async () => {
    if(selectedDocumentSteps[currentDocumentationSteps] === 'editDoc') {
      const document: any = documentState?.documentationContent;
      const articleLink = document?.article_link || ""
      if (articleLink) await handleAddArticleLinkToJira(articleLink);
    }
    resetUpload();
    dispatch(setCurrentDocumentationSteps(0));
    dispatch(setOpenCreateDocumentation(false))
    dispatch(setSelectedDocumentSteps(["modeSelection"]))
  }

  useEffect(() => {
    if (estimationTimer && triggerTimer)
      timerRef.current = window.setInterval(() => {
        setEstimationTimer(prev => {
          if (prev > 1) return prev - 1;

          if (timerRef.current) {
            window.clearInterval(timerRef.current);
            timerRef.current = null;
          }
          return prev;
        });
      }, 1000);
  }, [triggerTimer])

  useEffect(() => {
    cleanupDocsChannel.onmessage = (event) => {
      if (event.data === 'cleanup') {
        handleClosePopup();
      }
    };

    return () => {
      cleanupDocsChannel.close();
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [])

  const renderScreen = () => {
    if (selectedDocumentSteps[currentDocumentationSteps] === "modeSelection") {
      return <DocumentationModeSelection
        resetUpload={resetUpload}
      />
    } else if (selectedDocumentSteps[currentDocumentationSteps] === "uploadCode") {
      return <CodeUploader
        setBugsAndErrorHandlingReport={setBugsAndErrorHandlingReport}
        resetUpload={resetUpload}
        requiredIssueDetails={requiredIssueDetails}
        setSavedFile={setSavedFile}
      />
    } else if (selectedDocumentSteps[currentDocumentationSteps] === 'bugsAndErrorHandlingReport') {
      return <BugsAndErrorReport
        bugsAndErrorHandlingReport={bugsAndErrorHandlingReport}
        jsonConvertedContent={jsonConvertedContent}
        setJsonConvertedContent={setJsonConvertedContent}
        reportFeedback={reportFeedback}
        setReportFeedback={setReportFeedback}
        requiredIssueDetails={requiredIssueDetails}
        savedFile={savedFile}
        setCoreFunctionalities={setCoreFunctionalities}
      />
    } else if (selectedDocumentSteps[currentDocumentationSteps] === 'showFunctionalities') {
      return <ShowFunctionalities
        coreFunctionalities={coreFunctionalities}
      />
    } else if (selectedDocumentSteps[currentDocumentationSteps] === 'showEstimation') {
      return <ShowEstimation
        coreFunctionalities={coreFunctionalities}
        estimationTimer={estimationTimer}
        setEstimationTimer={setEstimationTimer}
        setTriggerTimer={setTriggerTimer}
      />
    } else if (selectedDocumentSteps[currentDocumentationSteps] === 'docInTinyMce') {
      return <DocInTinyMce
        handleClosePopup={handleClosePopup}
      />
    } else if (selectedDocumentSteps[currentDocumentationSteps] === 'editDoc') {
      return <EditDocument
        handleClosePopup={handleClosePopup}
        requiredIssueDetails={requiredIssueDetails}
        setArticleLink={setArticleLink}
      />
    } else if (selectedDocumentSteps[currentDocumentationSteps] === 'articlePage') {
      return <ArticleLink
        handleClosePopup={handleClosePopup}
        articleLink={articleLink}
      />
    } else {
      return <div></div>
    }
  }

  return (
    <Dialog disableEnforceFocus open={documentState.openCreateDocumentation} scroll="paper" fullWidth fullScreen={!["uploadCode", "showEstimation", "articlePage"].includes(selectedDocumentSteps[currentDocumentationSteps])} maxWidth="md" className='dialog-container'>
      <CloseIcon className='close-icon' onClick={handleClosePopup} />
      {renderScreen()}
    </Dialog>
  )
}

export default CreateDocumentation